import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, dateToISO, isoToDate } from "@/lib/dateUtils";
import { stayDaySet, overageDaySet } from "@/lib/schengenCalc";
import { useT } from "@/lib/i18n";

export default function CalendarView({ trips, result }) {
  const { t } = useT();
  const [month, setMonth] = useState(() => isoToDate(result.referenceDate));

  const rangeStart = addDays(result.windowStart, -30);
  const rangeEnd = addDays(result.windowEnd, 60);
  const stayDays = useMemo(
    () => stayDaySet(trips, rangeStart, rangeEnd, result.referenceDate),
    [trips, rangeStart, rangeEnd, result.referenceDate],
  );
  const overageDays = useMemo(
    () => overageDaySet(trips, rangeStart, rangeEnd, result.referenceDate),
    [trips, rangeStart, rangeEnd, result.referenceDate],
  );

  const modifiers = {
    schengen: (date) => {
      const iso = dateToISO(date);
      return stayDays.has(iso) && !overageDays.has(iso);
    },
    overage: (date) => overageDays.has(dateToISO(date)),
    window: (date) => {
      const iso = dateToISO(date);
      return iso >= result.windowStart && iso <= result.windowEnd;
    },
  };

  const modifiersClassNames = {
    schengen: "rdp-day_schengen",
    overage: "rdp-day_overage",
    window: "rdp-day_window",
  };

  return (
    <Card
      className="bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] w-full h-full flex flex-col"
      data-testid="calendar-view"
    >
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-serif-display text-xl text-white tracking-tight">
          {t("calendar.title")}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-slate-100"
            onClick={() =>
              setMonth((m) => {
                const d = new Date(m);
                d.setMonth(d.getMonth() - 1);
                return d;
              })
            }
            data-testid="cal-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-slate-300 text-xs px-2"
            onClick={() => setMonth(isoToDate(result.referenceDate))}
            data-testid="cal-today"
          >
            {t("calendar.today")}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-slate-100"
            onClick={() =>
              setMonth((m) => {
                const d = new Date(m);
                d.setMonth(d.getMonth() + 1);
                return d;
              })
            }
            data-testid="cal-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex justify-center">
        <Calendar
          mode="single"
          month={month}
          onMonthChange={setMonth}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          weekStartsOn={1}
          showOutsideDays
          className="[&_.rdp-caption_label]:font-serif-display [&_.rdp-caption_label]:text-lg [&_.rdp-day]:font-mono-terminal"
        />
      </div>

      <div className="mt-4 flex items-center gap-4 flex-wrap text-xs text-slate-400 justify-center">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-[#D4AF37]/40 border border-[#D4AF37]/40" />
          {t("calendar.legend.stay")}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-red-500/40 border border-red-500/40" />
          {t("calendar.legend.overage")}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-[#D4AF37]/70" />
          {t("calendar.legend.window")}
        </div>
      </div>
    </Card>
  );
}
